import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, CardActionArea, CardMedia, Grid, Chip,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  CircularProgress, Alert, IconButton, ImageList, ImageListItem, ToggleButtonGroup,
  ToggleButton, useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CallIcon from '@mui/icons-material/Call';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { listMarket, createListing, deleteListing, uploadListingPhoto } from '../api';

const CATEGORIES = {
  FARM_PRODUCE: 'Farm produce',
  LIVESTOCK: 'Livestock',
  FOOD: 'Food',
  FASHION: 'Fashion',
  ELECTRONICS: 'Electronics',
  SERVICES: 'Services',
  ART_CRAFT: 'Art & Craft',
  MUSIC_ENTERTAINMENT: 'Music & Entertainment',
  PROFESSIONAL: 'Professional',
  OTHER: 'Other',
};

const ADMIN_ROLES = ['ADMIN', 'PRESIDENT'];
const fmtKES = (n) => `KES ${Number(n || 0).toLocaleString()}`;

const waLink = (phone, title) =>
  `https://wa.me/${String(phone).replace(/\D/g, '')}?text=${encodeURIComponent(`Habari! I saw "${title}" on our Mbaa Mwendwa family app.`)}`;

const MarketPage = ({ currentUser }) => {
  const [listings, setListings] = useState([]);
  const [filter, setFilter] = useState('ALL'); // ALL | PRODUCT | TALENT
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listMarket(filter === 'ALL' ? undefined : filter);
      setListings(data);
      return data;
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the market.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const refreshSelected = async () => {
    const data = await load();
    if (selected) {
      const updated = data.find((l) => l._id === selected._id);
      if (updated) setSelected(updated);
    }
  };

  return (
    <Box sx={{ maxWidth: 950, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Market & Talents</Typography>
          <Typography variant="body2" color="text.secondary">
            Buy from family, sell to family — and show what you do best.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ background: 'linear-gradient(135deg, #059669 0%, #065F46 100%)', whiteSpace: 'nowrap' }}
        >
          {isMobile ? 'Post' : 'Post listing'}
        </Button>
      </Box>

      <ToggleButtonGroup
        value={filter}
        exclusive
        size="small"
        onChange={(e, v) => v && setFilter(v)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="ALL">All</ToggleButton>
        <ToggleButton value="PRODUCT">🛒 Products</ToggleButton>
        <ToggleButton value="TALENT">🌟 Talents</ToggleButton>
      </ToggleButtonGroup>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : listings.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 3, textAlign: 'center', py: 6 }}>
          <StorefrontIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          <Typography variant="h6">Nothing here yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Be the first to post a product or showcase a talent.
          </Typography>
          <Button variant="outlined" onClick={() => setCreateOpen(true)}>Post the first listing</Button>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {listings.map((l) => (
            <Grid item xs={6} sm={4} key={l._id}>
              <Card sx={{ borderRadius: 3, height: '100%' }}>
                <CardActionArea onClick={() => setSelected(l)} sx={{ height: '100%' }}>
                  {l.photos?.[0] ? (
                    <CardMedia component="img" height="120" image={l.photos[0]} alt={l.title} />
                  ) : (
                    <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f0f7' }}>
                      <StorefrontIcon sx={{ fontSize: 40, color: '#b0b0c8' }} />
                    </Box>
                  )}
                  <CardContent sx={{ p: 1.5 }}>
                    <Chip
                      label={l.kind === 'PRODUCT' ? '🛒 Product' : '🌟 Talent'}
                      size="small"
                      sx={{ mb: 0.5, bgcolor: l.kind === 'PRODUCT' ? '#059669' : '#065F46', color: '#fff', fontWeight: 600 }}
                    />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>{l.title}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {CATEGORIES[l.category] || 'Other'}{l.location ? ` • ${l.location}` : ''}
                    </Typography>
                    {l.price !== undefined && l.price !== null && (
                      <Typography variant="subtitle2" sx={{ color: '#059669', fontWeight: 700 }}>{fmtKES(l.price)}</Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <CreateListingDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async (created) => { setCreateOpen(false); await load(); setSelected(created); }}
      />

      {selected && (
        <ListingDetailDialog
          listing={selected}
          currentUser={currentUser}
          onClose={() => setSelected(null)}
          onChanged={refreshSelected}
          onDeleted={async () => { setSelected(null); await load(); }}
        />
      )}
    </Box>
  );
};

/* ============================ Create dialog ============================ */

const CreateListingDialog = ({ open, onClose, onCreated }) => {
  const [kind, setKind] = useState('PRODUCT');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [price, setPrice] = useState('');
  const [location, setLocation] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    setError('');
    setSaving(true);
    try {
      const created = await createListing({
        kind, title, description, category: category || 'OTHER',
        price: kind === 'PRODUCT' || price ? price : undefined,
        location, contactPhone: contactPhone || undefined,
      });
      setKind('PRODUCT'); setTitle(''); setDescription(''); setCategory('');
      setPrice(''); setLocation(''); setContactPhone('');
      onCreated(created);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not post your listing.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Post to the family market</DialogTitle>
      <DialogContent>
        <ToggleButtonGroup value={kind} exclusive size="small" sx={{ mb: 1, mt: 0.5 }}
          onChange={(e, v) => v && setKind(v)}>
          <ToggleButton value="PRODUCT">🛒 Product for sale</ToggleButton>
          <ToggleButton value="TALENT">🌟 Talent / service</ToggleButton>
        </ToggleButtonGroup>

        <TextField label="Title" fullWidth margin="dense" value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={kind === 'PRODUCT' ? 'e.g. Fresh mangoes — 1 crate' : 'e.g. Professional photography'} />
        <TextField select label="Category" fullWidth margin="dense" value={category}
          onChange={(e) => setCategory(e.target.value)}>
          {Object.entries(CATEGORIES).map(([key, label]) => (
            <MenuItem key={key} value={key}>{label}</MenuItem>
          ))}
        </TextField>
        {kind === 'PRODUCT' ? (
          <TextField label="Price (KES)" type="number" fullWidth margin="dense"
            value={price} onChange={(e) => setPrice(e.target.value)} />
        ) : (
          <TextField label="Rate (KES, optional)" type="number" fullWidth margin="dense"
            value={price} onChange={(e) => setPrice(e.target.value)}
            helperText="Leave empty if you prefer to discuss" />
        )}
        <TextField label="Location" fullWidth margin="dense" value={location}
          onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Kitui" />
        <TextField label="Details" fullWidth margin="dense" multiline minRows={3}
          value={description} onChange={(e) => setDescription(e.target.value)} />
        <TextField label="Contact number (optional)" fullWidth margin="dense"
          value={contactPhone} onChange={(e) => setContactPhone(e.target.value)}
          helperText="Buyers will contact this number — leave empty to use your registered phone" />
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving || !title.trim() || (kind === 'PRODUCT' && !price)}
          onClick={handleCreate}
          sx={{ background: 'linear-gradient(135deg, #059669 0%, #065F46 100%)' }}>
          {saving ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Post'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ============================ Detail dialog ============================ */

const ListingDetailDialog = ({ listing, currentUser, onClose, onChanged, onDeleted }) => {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const fileInput = useRef(null);

  const canManage =
    ADMIN_ROLES.includes(currentUser?.role) ||
    String(listing.postedBy?.user) === String(currentUser?.id || currentUser?._id);

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setBusy('photo');
    uploadListingPhoto(listing._id, file)
      .then(onChanged)
      .catch((err) => setError(err.response?.data?.error || 'Photo upload failed.'))
      .finally(() => setBusy(''));
    e.target.value = '';
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove this listing? This cannot be undone.')) return;
    setError('');
    setBusy('delete');
    try {
      await deleteListing(listing._id);
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove the listing.');
      setBusy('');
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        <Chip
          label={listing.kind === 'PRODUCT' ? '🛒 Product' : '🌟 Talent'}
          size="small"
          sx={{ mr: 1, bgcolor: listing.kind === 'PRODUCT' ? '#059669' : '#065F46', color: '#fff', fontWeight: 600 }}
        />
        {listing.title}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {[CATEGORIES[listing.category], listing.location].filter(Boolean).join(' • ')}
          {listing.postedBy?.name ? ` • Posted by ${listing.postedBy.name}` : ''}
        </Typography>
        {listing.price !== undefined && listing.price !== null && (
          <Typography variant="h6" sx={{ color: '#059669', fontWeight: 800, mb: 1 }}>{fmtKES(listing.price)}</Typography>
        )}
        {listing.description && (
          <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{listing.description}</Typography>
        )}

        {(listing.photos || []).length > 0 && (
          <ImageList cols={3} gap={6} sx={{ mb: 1 }}>
            {listing.photos.map((url) => (
              <ImageListItem key={url}>
                <img src={url} alt={listing.title} loading="lazy" style={{ borderRadius: 8 }} />
              </ImageListItem>
            ))}
          </ImageList>
        )}
        {canManage && (
          <>
            <Button size="small" startIcon={<AddPhotoAlternateIcon />} disabled={busy === 'photo'}
              onClick={() => fileInput.current?.click()}>
              {busy === 'photo' ? 'Uploading…' : 'Add photo'}
            </Button>
            <input type="file" accept="image/*" hidden ref={fileInput} onChange={handlePhoto} />
          </>
        )}

        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button
            fullWidth variant="contained" startIcon={<WhatsAppIcon />}
            href={waLink(listing.contactPhone, listing.title)} target="_blank" rel="noopener"
            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1ebe5b' }, fontWeight: 700 }}
          >
            WhatsApp seller
          </Button>
          <Button
            fullWidth variant="outlined" startIcon={<CallIcon />}
            href={`tel:${listing.contactPhone}`}
          >
            Call
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      {canManage && (
        <DialogActions>
          <Button color="error" disabled={busy === 'delete'} onClick={handleDelete}>
            Remove listing
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default MarketPage;
